import React, { useState, useCallback, useEffect } from "react";
import "./CuneiformPage.css";

// Allowed cuneiform characters ONLY
// Using String.fromCodePoint to ensure proper Unicode character creation
const ALLOWED_CUNEIFORM = {
  // Letters a-z
  a: String.fromCodePoint(0x103a0), // 𐎠
  b: String.fromCodePoint(0x103a1), // 𐎡
  c: String.fromCodePoint(0x103a2), // 𐎢
  d: String.fromCodePoint(0x103a3), // 𐎣
  e: String.fromCodePoint(0x103a4), // 𐎤
  f: String.fromCodePoint(0x103a5), // 𐎥
  g: String.fromCodePoint(0x103a6), // 𐎦
  h: String.fromCodePoint(0x103a7), // 𐎧
  i: String.fromCodePoint(0x103a8), // 𐎨
  j: String.fromCodePoint(0x103a9), // 𐎩
  k: String.fromCodePoint(0x103aa), // 𐎪
  l: String.fromCodePoint(0x103ab), // 𐎫
  m: String.fromCodePoint(0x103ac), // 𐎬
  n: String.fromCodePoint(0x103ad), // 𐎭
  o: String.fromCodePoint(0x103ae), // 𐎮
  p: String.fromCodePoint(0x103af), // 𐎯
  q: String.fromCodePoint(0x103b0), // 𐎰
  r: String.fromCodePoint(0x103b1), // 𐎱
  s: String.fromCodePoint(0x103b2), // 𐎲
  t: String.fromCodePoint(0x103b3), // 𐎳
  u: String.fromCodePoint(0x103b4), // 𐎴
  v: String.fromCodePoint(0x103b5), // 𐎵
  w: String.fromCodePoint(0x103b6), // 𐎶
  x: String.fromCodePoint(0x103b7), // 𐎷
  y: String.fromCodePoint(0x103b8), // 𐎸
  z: String.fromCodePoint(0x103b9), // 𐎹
  // Capital marker - special character to indicate next letter should be capitalized
  // Using a cuneiform character outside our letter range as marker
  _CAPITAL_MARKER: String.fromCodePoint(0x103ba), // 𐎺 - marks next letter as capital
  // Numbers 0-9 (keep as normal numbers)
  0: "0",
  1: "1",
  2: "2",
  3: "3",
  4: "4",
  5: "5",
  6: "6",
  7: "7",
  8: "8",
  9: "9",
  // Punctuation (keep as normal punctuation)
  ".": ".",
  ",": ",",
  "!": "!",
  "?": "?",
  ":": ":",
  ";": ";",
  "-": "-",
  "/": "/",
  "@": "@",
  "&": "&",
  "=": "=",
  "#": "#",
  "%": "%",
  // Space
  " ": " ",
};

// Create reverse mapping: cuneiform -> english
const CUNEIFORM_TO_ENGLISH = {};
Object.entries(ALLOWED_CUNEIFORM).forEach(([eng, cuneiform]) => {
  CUNEIFORM_TO_ENGLISH[cuneiform] = eng;
});

// Get array of allowed cuneiform characters (for encryption)
// Include letters, numbers, and punctuation - exclude only space
// Numbers and punctuation will be encrypted using the same seed-based algorithm
const CUNEIFORM_CHARS = Object.entries(ALLOWED_CUNEIFORM)
  .filter(([key, value]) => key !== " ")
  .map(([key, value]) => value);

// Simple hash function (SHA-256 would require crypto API, using simple hash for demo)
async function calculateHash(text) {
  if (!text) return "";

  // Use Web Crypto API for SHA-256
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex.substring(0, 16); // Show first 16 chars for brevity
}

// Custom encryption algorithm using only allowed cuneiform characters
function encryptToCuneiform(text, seed) {
  if (!text) return "";
  if (!seed) return "";

  // Process text character by character, preserving capitalization info
  // Filter to allowed characters and track which letters are capital
  const processedChars = [];
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const lowerChar = char.toLowerCase();

    // Check if it's an allowed character
    if (ALLOWED_CUNEIFORM.hasOwnProperty(lowerChar) || lowerChar === " ") {
      // If it's a letter and it's uppercase, add capital marker before lowercase version
      if (char >= "A" && char <= "Z" && lowerChar !== char) {
        processedChars.push(ALLOWED_CUNEIFORM._CAPITAL_MARKER);
        processedChars.push(lowerChar);
      } else {
        processedChars.push(lowerChar);
      }
    }
  }

  const normalizedText = processedChars.join("");
  if (!normalizedText) return "";

  // Create seed-based key stream
  const seedChars = seed.toLowerCase().split("");
  const seedLength = seedChars.length;

  // Calculate seed hash for additional obfuscation
  const seedHash = seedChars.reduce((acc, char) => acc + char.charCodeAt(0), 0);

  let encrypted = "";
  let charIndex = 0; // Track position in normalized text (for seed indexing)

  // Iterate by code points to handle surrogate pairs correctly
  for (let i = 0; i < normalizedText.length; ) {
    const codePoint = normalizedText.codePointAt(i);
    const char = String.fromCodePoint(codePoint);
    // Advance by 1 or 2 depending on if it's a surrogate pair
    i += codePoint > 0xffff ? 2 : 1;

    // Handle spaces - add double space to distinguish from separators
    if (char === " ") {
      encrypted += "  "; // Double space for real space
      charIndex++;
      continue;
    }

    // Get base cuneiform character
    // char might be a cuneiform character (for capital marker) or a key (for other chars)
    let baseCuneiform;
    if (CUNEIFORM_TO_ENGLISH[char]) {
      // It's already a cuneiform character (like capital marker), use it directly
      baseCuneiform = char;
    } else {
      // It's a key (like "a", "b", etc.), look it up
      baseCuneiform = ALLOWED_CUNEIFORM[char];
      if (!baseCuneiform) {
        charIndex++;
        continue;
      }
    }

    // Get seed character for this position (use charIndex, not i)
    const seedChar = seedChars[charIndex % seedLength];
    const seedValue = seedChar.charCodeAt(0);

    // Find index of base cuneiform in allowed chars
    // Use codePointAt to handle surrogate pairs correctly
    const baseCodePoint = baseCuneiform.codePointAt(0);
    let baseIndex = -1;
    for (let j = 0; j < CUNEIFORM_CHARS.length; j++) {
      const charCodePoint = CUNEIFORM_CHARS[j].codePointAt(0);
      if (charCodePoint === baseCodePoint) {
        baseIndex = j;
        break;
      }
    }
    if (baseIndex === -1) {
      charIndex++;
      continue;
    }

    // Apply encryption: shift based on seed
    const shift = (seedValue + seedHash + charIndex) % CUNEIFORM_CHARS.length;
    const encryptedIndex = (baseIndex + shift) % CUNEIFORM_CHARS.length;
    const encryptedChar = CUNEIFORM_CHARS[encryptedIndex];

    encrypted += encryptedChar;
    // Add separator between characters (single space) except before spaces or at the end
    if (i < normalizedText.length) {
      // Check if next character (by code point) is a space
      const nextCodePoint = normalizedText.codePointAt(i);
      const nextChar = nextCodePoint ? String.fromCodePoint(nextCodePoint) : "";
      if (nextChar !== " ") {
        encrypted += " ";
      }
    }
    charIndex++;
  }

  return encrypted;
}

// Custom decryption algorithm
function decryptFromCuneiform(encrypted, seed) {
  if (!encrypted) return "";
  if (!seed) return "";

  // Create seed-based key stream (same as encryption)
  const seedChars = seed.toLowerCase().split("");
  const seedLength = seedChars.length;

  // Calculate same seed hash
  const seedHash = seedChars.reduce((acc, char) => acc + char.charCodeAt(0), 0);

  let decrypted = "";
  let charIndex = 0; // Track character position (same as encryption)

  // Process character by character, handling spaces correctly
  // Real spaces are encoded as double spaces, separators are single spaces
  for (let i = 0; i < encrypted.length; ) {
    const char = encrypted[i];

    // Check for double space (real space in original)
    if (char === " " && i < encrypted.length - 1 && encrypted[i + 1] === " ") {
      decrypted += " ";
      charIndex++;
      i += 2; // Skip both spaces
      continue;
    }

    // Skip single spaces (separators between cuneiform chars)
    if (char === " ") {
      i++;
      continue;
    }

    // Process character using code point iteration
    // This handles both cuneiform characters (surrogate pairs) and regular ASCII (numbers/punctuation)
    const codePoint = encrypted.codePointAt(i);
    // Advance by 1 or 2 depending on if it's a surrogate pair
    i += codePoint > 0xffff ? 2 : 1;

    // Find encrypted character in allowed chars (now includes numbers and punctuation)
    let encryptedIndex = -1;
    for (let j = 0; j < CUNEIFORM_CHARS.length; j++) {
      const charCodePoint = CUNEIFORM_CHARS[j].codePointAt(0);
      if (charCodePoint === codePoint) {
        encryptedIndex = j;
        break;
      }
    }
    if (encryptedIndex === -1) {
      // Skip invalid characters
      continue;
    }

    // Get seed character for this position (use charIndex)
    const seedChar = seedChars[charIndex % seedLength];
    const seedValue = seedChar.charCodeAt(0);

    // Reverse the encryption: subtract the shift
    const shift = (seedValue + seedHash + charIndex) % CUNEIFORM_CHARS.length;
    let baseIndex = (encryptedIndex - shift) % CUNEIFORM_CHARS.length;
    if (baseIndex < 0) {
      baseIndex += CUNEIFORM_CHARS.length;
    }

    const baseCuneiform = CUNEIFORM_CHARS[baseIndex];

    // Map back to English
    const englishChar = CUNEIFORM_TO_ENGLISH[baseCuneiform];
    if (englishChar) {
      // Check if this is a capital marker
      if (englishChar === "_CAPITAL_MARKER") {
        // Next character should be capitalized
        // Get the next character (skip separator spaces)
        let nextI = i;
        while (nextI < encrypted.length && encrypted[nextI] === " ") {
          nextI++;
        }
        if (nextI < encrypted.length) {
          const nextCodePoint = encrypted.codePointAt(nextI);
          nextI += nextCodePoint > 0xffff ? 2 : 1;

          // Find and decrypt next character
          let nextEncryptedIndex = -1;
          for (let j = 0; j < CUNEIFORM_CHARS.length; j++) {
            const charCodePoint = CUNEIFORM_CHARS[j].codePointAt(0);
            if (charCodePoint === nextCodePoint) {
              nextEncryptedIndex = j;
              break;
            }
          }

          if (nextEncryptedIndex !== -1) {
            // Use charIndex + 1 for the next character's position in normalized text
            const nextSeedChar = seedChars[(charIndex + 1) % seedLength];
            const nextSeedValue = nextSeedChar.charCodeAt(0);
            const nextShift =
              (nextSeedValue + seedHash + charIndex + 1) %
              CUNEIFORM_CHARS.length;
            let nextBaseIndex =
              (nextEncryptedIndex - nextShift) % CUNEIFORM_CHARS.length;
            if (nextBaseIndex < 0) nextBaseIndex += CUNEIFORM_CHARS.length;
            const nextBaseCuneiform = CUNEIFORM_CHARS[nextBaseIndex];
            const nextEnglishChar = CUNEIFORM_TO_ENGLISH[nextBaseCuneiform];

            if (nextEnglishChar && nextEnglishChar !== "_CAPITAL_MARKER") {
              // If it's a letter, capitalize it; otherwise just add it
              if (nextEnglishChar >= "a" && nextEnglishChar <= "z") {
                decrypted += nextEnglishChar.toUpperCase();
              } else {
                decrypted += nextEnglishChar;
              }
              charIndex += 2; // Skip both marker and the capitalized letter in normalized text
              i = nextI; // Update position in encrypted string
              continue; // Skip the increment at end of loop
            }
          }
        }
        // If we can't capitalize next, just skip the marker
        charIndex++;
        continue; // Skip the increment at end of loop
      } else {
        decrypted += englishChar;
      }
    }
    charIndex++; // Increment after processing character
  }

  return decrypted;
}

// Export functions for testing
export { encryptToCuneiform, decryptFromCuneiform, ALLOWED_CUNEIFORM };

const CuneiformPage = () => {
  const [encodeText, setEncodeText] = useState("hello world");
  const [decodeText, setDecodeText] = useState("");
  const [seedText, setSeedText] = useState("secret");
  const [encodedResult, setEncodedResult] = useState("");
  const [decodedResult, setDecodedResult] = useState("");
  const [encodedHash, setEncodedHash] = useState("");
  const [decodedHash, setDecodedHash] = useState("");
  const [encodeInputHash, setEncodeInputHash] = useState("");
  const [decodeInputHash, setDecodeInputHash] = useState("");
  const [error, setError] = useState(null);
  const [hashMatch, setHashMatch] = useState(null); // null = not checked yet, true/false = verified/failed

  // Calculate hash for encode input text
  useEffect(() => {
    const updateEncodeInputHash = async () => {
      if (encodeText.trim()) {
        const hash = await calculateHash(encodeText);
        setEncodeInputHash(hash);
      } else {
        setEncodeInputHash("");
      }
    };
    updateEncodeInputHash();
  }, [encodeText]);

  // Calculate hash for decode input text
  useEffect(() => {
    const updateDecodeInputHash = async () => {
      if (decodeText.trim()) {
        const hash = await calculateHash(decodeText);
        setDecodeInputHash(hash);
      } else {
        setDecodeInputHash("");
      }
    };
    updateDecodeInputHash();
  }, [decodeText]);

  const handleEncode = useCallback(async () => {
    if (!encodeText.trim()) {
      setError("Please enter text to encode");
      return;
    }

    if (!seedText.trim()) {
      setError("Please enter a seed text for security");
      return;
    }

    setError(null);

    try {
      // Encrypt the text
      const encrypted = encryptToCuneiform(encodeText, seedText);
      setEncodedResult(encrypted);

      // Use ENCRYPTED text for hash (hash of the result string)
      const hash = await calculateHash(encrypted);
      setEncodedHash(hash);

      // Copy encrypted text to decode field and auto-decode
      setDecodeText(encrypted);

      // Automatically decrypt to verify
      const decrypted = decryptFromCuneiform(encrypted, seedText);
      setDecodedResult(decrypted);

      // Calculate hash of decrypted text (should match filtered original)
      const decryptedHash = await calculateHash(decrypted);
      setDecodedHash(decryptedHash);

      // Calculate expected hash from filtered original text (same as encryption does)
      const filteredOriginal = encodeText
        .split("")
        .filter((char) => {
          const lowerChar = char.toLowerCase();
          return (
            ALLOWED_CUNEIFORM.hasOwnProperty(lowerChar) || lowerChar === " "
          );
        })
        .join("");
      const expectedHash = await calculateHash(filteredOriginal);

      // Check if hashes match
      setHashMatch(decryptedHash === expectedHash);
    } catch (err) {
      setError(`Encoding error: ${err.message}`);
      console.error(err);
      setHashMatch(false);
    }
  }, [encodeText, seedText]);

  const handleDecode = useCallback(async () => {
    if (!decodeText.trim()) {
      setError("Please enter cuneiform text to decode");
      return;
    }

    if (!seedText.trim()) {
      setError("Please enter the seed text used for encoding");
      return;
    }

    setError(null);

    try {
      // Decrypt the text
      const decrypted = decryptFromCuneiform(decodeText, seedText);
      setDecodedResult(decrypted);

      // Use DECRYPTED text for hash (original string)
      const hash = await calculateHash(decrypted);
      setDecodedHash(hash);
    } catch (err) {
      setError(`Decoding error: ${err.message}`);
      console.error(err);
    }
  }, [decodeText, seedText]);

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setError(null);
      // Could add a success message here
    } catch (err) {
      setError(`Failed to copy ${type}`);
    }
  };

  const handleDownload = (text, filename) => {
    try {
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setError(null);
    } catch (err) {
      setError(`Failed to download file: ${err.message}`);
    }
  };

  const handleDownloadBoth = () => {
    if (!encodedResult || !decodedResult) return;

    const content = `Encoded Result:\n${encodedResult}\n\nDecoded Result:\n${decodedResult}\n\nHash (Encoded): ${encodedHash}\nHash (Decoded): ${decodedHash}`;
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    handleDownload(content, `cuneiform-results-${timestamp}.txt`);
  };

  const generateRandomSeed = () => {
    // Generate a random string of 12 English letters
    const length = 12;
    const letters = "abcdefghijklmnopqrstuvwxyz";
    let randomString = "";
    for (let i = 0; i < length; i++) {
      randomString += letters.charAt(
        Math.floor(Math.random() * letters.length)
      );
    }
    setSeedText(randomString);
  };

  return (
    <div className="cuneiform-container">
      <div className="cuneiform-content">
        <h1 className="cuneiform-title">
          {/* <span className="cuneiform-icon">🔐</span> */}
          Cuneiform Transcoder
        </h1>
        {/* <p className="cuneiform-subtitle">
          Encode and decode messages using Old Persian cuneiform script with seed-based security
        </p> */}

        {/* Seed Text Input */}
        <div className="cuneiform-seed-section">
          <input
            type="text"
            className="cuneiform-input"
            placeholder="Enter seed text (English) for security"
            value={seedText}
            onChange={(e) => setSeedText(e.target.value)}
            style={{ flex: 1, minWidth: "200px" }}
          />
          <button
            className="cuneiform-button cuneiform-generate-button"
            onClick={generateRandomSeed}
            title="Generate random seed"
          >
            Random Seed
          </button>
        </div>

        {/* Encode Section */}
        <div className="cuneiform-section">
          <h2 className="cuneiform-section-title">Encode</h2>
          <div className="cuneiform-input-section">
            <textarea
              className="cuneiform-textarea"
              placeholder="Enter text to encode (English)"
              value={encodeText}
              onChange={(e) => setEncodeText(e.target.value)}
              rows={4}
            />
            <button className="cuneiform-button" onClick={handleEncode}>
              Encode
            </button>
          </div>
          {encodeInputHash && (
            <div className="cuneiform-input-hash">
              <strong>Input Hash:</strong> <code>{encodeInputHash}</code>
            </div>
          )}
          {encodedResult && hashMatch !== null && (
            <div className="cuneiform-hash-status">
              <strong>Verification:</strong>{" "}
              {hashMatch ? (
                <span className="cuneiform-verified">[Verified]</span>
              ) : (
                <span className="cuneiform-failed">[Failed]</span>
              )}
            </div>
          )}
          {encodedResult && (
            <div className="cuneiform-result-card">
              <div className="cuneiform-result-header">
                <h3>Encoded Result</h3>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <button
                    className="cuneiform-copy-button"
                    onClick={() => handleCopy(encodedResult, "encoded")}
                    title="Copy encoded text"
                  >
                    [Copy]
                  </button>
                  <button
                    className="cuneiform-copy-button"
                    onClick={() =>
                      handleDownload(encodedResult, "encoded-result.txt")
                    }
                    title="Download encoded text"
                  >
                    [Download]
                  </button>
                </div>
              </div>
              <div className="cuneiform-result-text">{encodedResult}</div>
              <div className="cuneiform-hash">
                <strong>Hash:</strong> <code>{encodedHash}</code>
              </div>
            </div>
          )}
        </div>

        {/* Decode Section */}
        <div className="cuneiform-section">
          <h2 className="cuneiform-section-title">Decode</h2>
          <div className="cuneiform-input-section">
            <textarea
              className="cuneiform-textarea"
              placeholder="Enter cuneiform text to decode"
              value={decodeText}
              onChange={(e) => setDecodeText(e.target.value)}
              rows={4}
            />
            <button className="cuneiform-button" onClick={handleDecode}>
              Decode
            </button>
          </div>
          {decodeInputHash && (
            <div className="cuneiform-input-hash">
              <strong>Input Hash:</strong> <code>{decodeInputHash}</code>
            </div>
          )}
          {decodedResult && (
            <div className="cuneiform-result-card">
              <div className="cuneiform-result-header">
                <h3>Decoded Result</h3>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  {hashMatch && (
                    <span
                      className="cuneiform-checkmark"
                      title="Hash verified - algorithm working correctly"
                    >
                      [OK]
                    </span>
                  )}
                  <button
                    className="cuneiform-copy-button"
                    onClick={() => handleCopy(decodedResult, "decoded")}
                    title="Copy decoded text"
                  >
                    [Copy]
                  </button>
                  <button
                    className="cuneiform-copy-button"
                    onClick={() =>
                      handleDownload(decodedResult, "decoded-result.txt")
                    }
                    title="Download decoded text"
                  >
                    [Download]
                  </button>
                </div>
              </div>
              <div className="cuneiform-result-text">{decodedResult}</div>
              <div className="cuneiform-hash">
                <strong>Hash:</strong> <code>{decodedHash}</code>
                {hashMatch && (
                  <span
                    className="cuneiform-hash-match"
                    title="Hash matches original - verification passed"
                  >
                    {" "}
                    [Verified]
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Download Both Results */}
        {encodedResult && decodedResult && (
          <div className="cuneiform-section">
            <button
              className="cuneiform-button"
              onClick={handleDownloadBoth}
              title="Download both encoded and decoded results"
            >
              Download Both Results
            </button>
          </div>
        )}

        {error && <div className="cuneiform-error">{error}</div>}
      </div>
    </div>
  );
};

export default CuneiformPage;
